import { formatDateTimeByLocale } from '@doss/common-all'
import { Box, Popover, Typography } from '@mui/material'
import { RxCross2 } from 'react-icons/rx'
import { useGlobalContext } from '../../../../common/hooks/useGlobalContext'
import { useTypedTranslation } from '../../../../common/hooks/useTypedTranslation'
import { gray } from '../../../../common/styles/colors'
import { FileCellDocument, FileCellSignature } from '../../../records/record-cell-details/types'
import { IconButton } from '../../atoms/icon-button/IconButton'

interface SignatureChipPopoverProps {
  open: boolean
  onClose: () => void
  anchorEl: HTMLElement | null
  signature: FileCellSignature
  document: FileCellDocument
}

/**
 * Renders a signature image and details in a popover when you click on a signature chip
 */
export function SignatureChipPopover({
  open,
  onClose,
  anchorEl,
  signature,
  document,
}: SignatureChipPopoverProps): React.ReactElement {
  const { t } = useTypedTranslation()
  const { locale } = useGlobalContext()
  const userName = signature.user
    ? `${signature.user.firstName} ${signature.user.lastName}`
    : undefined
  const timestamp = formatDateTimeByLocale(signature.createdAt, locale)

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: -16,
        horizontal: 'left',
      }}
      disableRestoreFocus
      onClose={onClose}
      open={open}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          margin: 1,
          padding: 0.5,
          width: 175,
        }}
      >
        <Box>
          {/* Curve at the top */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              insetInlineStart: 0,
              width: '30%',
              height: '100%',
              borderTop: `1px solid ${gray[4]}`,
              borderInlineStart: `1px solid ${gray[4]}`,
              borderEndStartRadius: '6px',
              borderStartStartRadius: '6px',
            }}
          />
          {userName && (
            <Typography sx={{ fontSize: '8px', paddingInlineStart: 0.5 }} variant="caption">
              {t('signature_chip.signed_by', { name: userName })}
            </Typography>
          )}
          <Box
            alt={document.name}
            component="img"
            crossOrigin="anonymous"
            src={document.storageUrl}
            sx={{ maxWidth: 100, display: 'block', margin: 0.5 }}
          />
          <Typography sx={{ fontSize: '8px', paddingInlineStart: 0.5 }} variant="caption">
            {timestamp}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              insetInlineStart: 0,
              width: '30%',
              height: '100%',
              borderBottom: `1px solid ${gray[4]}`,
              borderInlineStart: `1px solid ${gray[4]}`,
              borderEndStartRadius: '6px',
              borderStartStartRadius: '6px',
            }}
          />
        </Box>

        <IconButton
          className="h-fit"
          onClick={onClose}
          size={'small'}
          sx={{
            position: 'absolute',
            top: -2,
            insetInlineEnd: -2,
          }}
          variant={'ghost'}
        >
          <RxCross2 />
        </IconButton>
      </Box>
    </Popover>
  )
}
